/**
 * THE LIVE RESEARCH STRIP — ONE LINE, ABOVE THE COMPOSER, DRIVEN BY THE RECORD.
 *
 * The Owner's approved shape: activity icon, a short state, the remaining time,
 * one line of current activity, and a chevron. It is deliberately one line at
 * every width: on a phone the activity truncates and the state and countdown
 * keep their room, because "is it running and for how long" is the question
 * the strip exists to answer.
 *
 * NOTHING HERE IS SIMULATED. The phase and its words come from rows the
 * research engine writes as it works (`scout_research_run_activity`). When no
 * phase row exists the strip says "Waiting for the next research step" and
 * does not turn the ring. When the phase is old, the ring stops even though
 * the window is still open — the record cannot tell a long step from a stuck
 * lane, and pretending otherwise is the fabricated progress this product
 * forbids.
 *
 * THE COMPLETION NOTICE IS NOT A REPORT. The brief is explicit: a finished
 * window posts a concise, truthful notice and a working link, never a source
 * list or a report into the canonical conversation. The report lives in
 * Research and its records in Discoveries.
 */

import { useEffect, useRef, useState } from 'preact/hooks';
import type { ComponentChildren } from 'preact';
import { navigate } from '../router';
import { parsePath } from '../paths';
import {
	RESEARCH_NOTICE_WINDOW_MS,
	RESEARCH_PHASE_LIVE_MS,
	RESEARCH_POLL_ACTIVE_MS,
	RESEARCH_POLL_IDLE_MS,
	RESEARCH_RESULT_LINKS,
	formatRemaining,
	readResearchStatus,
	readingOf,
	remainingMs,
	scoutOwnActivity,
	serverNowAt,
	stripActivityText,
	type DelegationWorkerActivity,
	type ResearchStatusPayload,
} from '../research-status';
import { Technical } from './about';

const PHASE_LABELS: Record<string, string> = {
	WAITING: 'Waiting',
	SELECTING: 'Choosing',
	PLANNING: 'Planning',
	SEARCHING: 'Searching',
	READING: 'Reading',
	ANALYZING: 'Analyzing',
	RECORDING: 'Recording',
	FINISHING: 'Finishing',
	ENDED: 'Ended',
	// THE SPECIALIST PHASES ARE PART OF THE SAME RECORDED SEQUENCE. Naming them
	// is what stops the panel showing a raw `PLANNING_WORKERS` tag.
	PLANNING_WORKERS: 'Planning agents',
	DELEGATING: 'Dispatching agents',
	RETRIEVING: 'Retrieving for agents',
	WORKER: 'Agent',
	RECONCILING: 'Reconciling agents',
	WORKERS_DECLINED: 'Agents declined',
	AUTHORING: 'Authoring',
	SELECTION_FAILED: 'Selection failed',
	RECOVERY: 'Recovering',
};

const ROLE_LABELS: Record<string, string> = {
	RETRIEVAL: 'Retrieval Agent',
	ANALYSIS: 'Analysis Agent',
	EXPERIMENT_DESIGN: 'Experiment Design Agent',
	CRITICAL_REVIEW: 'Critical Review Agent',
};

const WORKER_STATE_LABELS: Record<DelegationWorkerActivity['state'], string> = {
	ACTIVE: 'Active',
	COMPLETED: 'Completed',
	INCOMPLETE: 'Completed with limits',
	FAILED: 'Failed',
	REFUSED: 'Refused',
	NOT_STARTED: 'Did not start',
	CANCELLED: 'Cancelled',
	UNKNOWN: 'Outcome not recorded',
};

function roleLabel(role: string): string {
	const key = role.toUpperCase();
	return ROLE_LABELS[key] ?? `${key.replace(/_/g, ' ').toLowerCase()} agent`;
}

function timeOf(value: string | null): string {
	if (value === null) return 'no recorded time';
	const at = new Date(value);
	if (Number.isNaN(at.getTime())) return value;
	return new Intl.DateTimeFormat(undefined, {
		hour: '2-digit',
		minute: '2-digit',
		second: '2-digit',
	}).format(at);
}

function Link({ href, children }: { href: string; children: ComponentChildren }) {
	return (
		<a
			href={href}
			class="research-strip-link"
			onClick={(event: MouseEvent) => {
				if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
				event.preventDefault();
				navigate(parsePath(href));
			}}
		>
			{children}
		</a>
	);
}

export function ResearchStrip({
	onEnded,
}: {
	/** Called once when a run the strip was showing stops being active. The chat
	 *  uses it to fetch the summary message the closing already posted. */
	onEnded?: (runId: string) => void;
} = {}) {
	const [payload, setPayload] = useState<ResearchStatusPayload | null>(null);
	const [receivedAt, setReceivedAt] = useState(0);
	const [now, setNow] = useState(() => Date.now());
	const [open, setOpen] = useState(false);
	const [dismissed, setDismissed] = useState<string | null>(null);
	const activeRef = useRef(false);
	const activeRunIdRef = useRef<string | null>(null);
	const mounted = useRef(true);
	const onEndedRef = useRef(onEnded);
	useEffect(() => {
		onEndedRef.current = onEnded;
	}, [onEnded]);

	useEffect(() => {
		mounted.current = true;
		let timer: ReturnType<typeof setTimeout> | null = null;
		let current = 0;

		const read = async (): Promise<void> => {
			if (document.visibilityState === 'hidden') {
				timer = setTimeout(() => void read(), RESEARCH_POLL_IDLE_MS);
				return;
			}
			const result = await readResearchStatus();
			if (!mounted.current) return;
			if (result.ok) {
				const nextRunId = result.data.active?.runId ?? null;
				const endedRunId = activeRunIdRef.current;
				activeRunIdRef.current = nextRunId;
				if (endedRunId !== null && nextRunId === null) {
					// THE SUMMARY IS ALREADY DURABLE. The closing posted it before the
					// window ended; this only asks the chat to read the newest page.
					onEndedRef.current?.(endedRunId);
				}
				setPayload(result.data);
				setReceivedAt(Date.now());
				activeRef.current = result.data.active !== null;
			}
			current = activeRef.current ? RESEARCH_POLL_ACTIVE_MS : RESEARCH_POLL_IDLE_MS;
			timer = setTimeout(() => void read(), current);
		};

		void read();
		const onVisible = () => {
			if (document.visibilityState !== 'visible') return;
			if (timer !== null) clearTimeout(timer);
			timer = null;
			void read();
		};
		document.addEventListener('visibilitychange', onVisible);
		return () => {
			mounted.current = false;
			if (timer !== null) clearTimeout(timer);
			document.removeEventListener('visibilitychange', onVisible);
		};
	}, []);

	// A SECOND HAND, ONLY WHILE A COUNTDOWN IS ON SCREEN. The strip does not
	// tick when there is no deadline to tick against.
	const hasCountdown =
		payload?.active !== null && payload?.active !== undefined && payload.active.state === 'RUNNING';
	useEffect(() => {
		if (!hasCountdown) return;
		const timer = setInterval(() => setNow(Date.now()), 1_000);
		return () => clearInterval(timer);
	}, [hasCountdown]);

	if (payload === null || !payload.wired) return null;
	const latestEnded = payload.latest;
	const latestIsRecent =
		latestEnded !== null &&
		latestEnded.endedAt !== null &&
		Date.now() - Date.parse(latestEnded.endedAt) <= RESEARCH_NOTICE_WINDOW_MS;
	const run = payload.active ?? (latestIsRecent ? latestEnded : null);
	if (run === null) return null;
	if (payload.active === null && dismissed === run.runId) return null;
	const reading = readingOf(run);
	const remaining = remainingMs(run, payload, now, receivedAt);
	const showCountdown = reading.kind === 'live' && remaining > 0;
	const ended = reading.kind === 'ended' || reading.kind === 'unobserved';
	// THE ONE LINE SHOWS RESEARCH AND AGENT ACTIVITY TOGETHER, FROM RECORDS.
	// A live window uses the reducer's line; a terminal notice keeps its own
	// sentence and appends the agents' final state only when records have one.
	const serverNowMs = serverNowAt(payload, receivedAt, now);
	const delegationLine = run.delegation?.line ?? null;
	const detail =
		reading.kind === 'live'
			? stripActivityText(run, serverNowMs)
			: delegationLine === null
				? reading.detail
				: `${reading.detail.replace(/[.…]+$/, '')} · ${delegationLine}`;
	const delegation = run.delegation;
	const scoutOwn = scoutOwnActivity(run);
	const scoutOwnAge =
		scoutOwn === null ? null : serverNowMs - Date.parse(scoutOwn.at);
	const scoutOwnRecent =
		scoutOwnAge !== null &&
		Number.isFinite(scoutOwnAge) &&
		scoutOwnAge >= -5_000 &&
		scoutOwnAge <= RESEARCH_PHASE_LIVE_MS;

	return (
		<div
			class={`research-strip research-strip--${reading.kind}`}
			role="group"
			aria-label="Research status"
			onClick={(event: MouseEvent) => {
				// THE WHOLE STRIP IS A TARGET, BUT ITS OWN CONTROLS ARE NOT. A click
				// on a link, the dismiss button or the toggle must do what it says
				// rather than also opening the panel. The toggle remains the
				// keyboard-accessible control.
				const target = event.target as HTMLElement | null;
				if (target !== null && target.closest('a,button') !== null) return;
				setOpen((value) => !value);
			}}
		>
			<span
				class={`research-strip-ring${reading.moving ? ' is-moving' : ''}${reading.kind === 'ended' && reading.failed ? ' is-failed' : ''}`}
				aria-hidden="true"
			/>
			<span class="research-strip-state">{reading.label}</span>
			{showCountdown && (
				<span class="research-strip-time" aria-label={`${formatRemaining(remaining)} remaining`}>
					{formatRemaining(remaining)} left
				</span>
			)}
			<span class="research-strip-detail" aria-live="polite" title={detail}>
				{detail}
			</span>
			{ended && (
				<span class="research-strip-links">
					{RESEARCH_RESULT_LINKS.map((link) => (
						<Link key={link.href} href={link.href}>
							{link.label}
						</Link>
					))}
					<button
						type="button"
						class="research-strip-dismiss"
						aria-label="Dismiss this research notice"
						onClick={() => setDismissed(run.runId)}
					>
						×
					</button>
				</span>
			)}
			<button
				type="button"
				class="research-strip-toggle"
				aria-expanded={open}
				aria-label={open ? 'Hide research detail' : 'Show research detail'}
				onClick={() => setOpen((value) => !value)}
			>
				<span aria-hidden="true">{open ? '▴' : '▾'}</span>
			</button>
			{open && (
				<div
					class="research-strip-panel"
					onClick={(event: MouseEvent) => {
						// A CLICK INSIDE THE PANEL IS READING, NOT CLOSING. The strip's
						// own root handler would otherwise collapse the panel when the
						// Owner selects text or opens the technical record.
						event.stopPropagation();
					}}
				>
					<p class="small">
						<span class="muted">Window:</span> {reading.label.toLowerCase()} ·{' '}
						{run.segmentsRun} step{run.segmentsRun === 1 ? '' : 's'} run
						{run.state === 'RUNNING' && showCountdown
							? ` · ${formatRemaining(remaining)} remaining`
							: ''}
					</p>
					{delegation !== undefined && delegation.observed && (
						<section class="research-strip-agents" aria-label="Specialist activity">
							<p class="small">
								<strong>Research activity</strong>
							</p>
							<ul class="research-strip-agent-list">
								<li>
									<span class="tag">Scout</span>{' '}
									<span>
										{scoutOwn?.detail ??
											(delegation.phase === 'WORKING'
												? 'Monitoring the specialist agents.'
												: 'Between recorded steps.')}
									</span>{' '}
									<span
										class={`agent-state ${scoutOwnRecent ? 'agent-state-active' : 'agent-state-unknown'}`}
									>
										{scoutOwn === null
											? 'No current step'
											: scoutOwnRecent
												? 'Current step'
												: 'Last recorded step'}
									</span>
								</li>
								{delegation.workers.map((worker) => (
									<li key={`${worker.role}-${String(worker.index)}`}>
										<span class="tag">{roleLabel(worker.role)}</span>{' '}
										<span>{worker.detail}</span>{' '}
										<span
											class={`agent-state agent-state-${worker.state.toLowerCase()}`}
										>
											{WORKER_STATE_LABELS[worker.state]}
										</span>
									</li>
								))}
							</ul>
							{delegation.workers.length === 0 && (
								<p class="muted small">
									{delegation.phase === 'PLANNING'
										? 'The window is planning specialist agents; none has started yet.'
										: delegation.phase === 'DISPATCHING'
											? 'Agents are being dispatched; no worker has recorded a start yet.'
											: 'No worker start or result is on record for this window.'}
								</p>
							)}
							{delegation.runs.length > 0 && (
								<p class="muted small">
									{delegation.runs
										.map(
											(entry) =>
												`${String(entry.roles.length)} agent${entry.roles.length === 1 ? '' : 's'} · ${entry.state.toLowerCase()}`,
										)
										.join(' · ')}
								</p>
							)}
						</section>
					)}
					<ol class="research-strip-steps">
						{[...run.activity].reverse().map((entry, index) => (
							<li key={`${entry.at}-${index}`}>
								<span class="tag">{PHASE_LABELS[entry.phase] ?? entry.phase}</span>{' '}
								<span>{entry.detail}</span>{' '}
								<span class="muted small">{timeOf(entry.at)}</span>
							</li>
						))}
					</ol>
					{run.activity.length === 0 && (
						<p class="muted small">
							No step has been recorded yet. The first step is due on the driver’s next tick.
						</p>
					)}
					<Technical summary="Technical record">
						<p class="small">
							Window <code>{run.runId}</code> · state <code>{run.state}</code> · deadline{' '}
							<code>{run.deadlineAt}</code>
							{run.endReason !== null && (
								<>
									{' '}
									· ended as <code>{run.endReason}</code>
								</>
							)}
						</p>
						{run.terminalReason !== null && <p class="small">{run.terminalReason}</p>}
					</Technical>
				</div>
			)}
		</div>
	);
}
